import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TrainingMatrix from './components/TrainingMatrix';
import ComplianceDashboard from './components/ComplianceDashboard';
import EmployeeDetailModal from './components/EmployeeDetailModal';
import CertificateUploadModal from './components/CertificateUploadModal';
import ComplianceReports from './components/ComplianceReports';

const TrainingCompliance = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadEmployee, setUploadEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');

  // Mock data for employees and their training compliance
  const mockEmployees = [
    {
      id: 1,
      name: "John Smith",
      role: "Safety Coordinator",
      department: "Operations",
      email: "john.smith@healthguard.com",
      certifications: [
        {
          name: "First Aid/CPR",
          provider: "American Red Cross",
          status: "current",
          issueDate: "2024-03-15",
          expiryDate: "2026-03-15",
          certificateId: "ARC-2024-001234"
        },
        {
          name: "OSHA 30-Hour",
          provider: "OSHA Training Institute",
          status: "expiring",
          issueDate: "2022-09-20",
          expiryDate: "2024-09-20",
          certificateId: "OSHA-30-2022-5678"
        },
        {
          name: "Hazmat Handling",
          provider: "DOT Training Center",
          status: "current",
          issueDate: "2023-11-10",
          expiryDate: "2025-11-10",
          certificateId: "DOT-HAZ-2023-9012"
        }
      ],
      trainingHistory: [
        {
          course: "Advanced Safety Management",
          provider: "Safety Institute",
          completionDate: "2024-08-15",
          score: 95
        },
        {
          course: "Emergency Response Training",
          provider: "Emergency Training Corp",
          completionDate: "2024-06-20",
          score: 88
        }
      ],
      requirements: [
        {
          name: "Annual Safety Refresher",
          completed: true,
          completionDate: "2024-08-15",
          dueDate: "2024-12-31"
        },
        {
          name: "Equipment Inspection Certification",
          completed: false,
          dueDate: "2024-10-30"
        }
      ]
    },
    {
      id: 2,
      name: "Sarah Johnson",
      role: "Manufacturing Supervisor",
      department: "Manufacturing",
      email: "sarah.johnson@healthguard.com",
      certifications: [
        {
          name: "Machine Safety Training",
          provider: "Manufacturing Safety Council",
          status: "current",
          issueDate: "2024-01-20",
          expiryDate: "2025-01-20",
          certificateId: "MSC-2024-3456"
        },
        {
          name: "Lockout/Tagout",
          provider: "Industrial Safety Training",
          status: "expired",
          issueDate: "2021-05-15",
          expiryDate: "2024-05-15",
          certificateId: "IST-LOTO-2021-7890"
        }
      ],
      trainingHistory: [
        {
          course: "Lean Manufacturing Principles",
          provider: "Manufacturing Excellence",
          completionDate: "2024-07-10",
          score: 92
        }
      ],
      requirements: [
        {
          name: "Quality Control Certification",
          completed: true,
          completionDate: "2024-07-10",
          dueDate: "2024-12-31"
        },
        {
          name: "Environmental Compliance Training",
          completed: false,
          dueDate: "2024-11-15"
        }
      ]
    },
    {
      id: 3,
      name: "Mike Chen",
      role: "Maintenance Technician",
      department: "Maintenance",
      email: "mike.chen@healthguard.com",
      certifications: [
        {
          name: "Electrical Safety",
          provider: "Electrical Training Alliance",
          status: "current",
          issueDate: "2023-08-10",
          expiryDate: "2025-08-10",
          certificateId: "ETA-2023-1122"
        },
        {
          name: "Confined Space Entry",
          provider: "Safety Training Solutions",
          status: "expiring",
          issueDate: "2022-10-05",
          expiryDate: "2024-10-05",
          certificateId: "STS-CSE-2022-3344"
        }
      ],
      trainingHistory: [
        {
          course: "Advanced Electrical Systems",
          provider: "Technical Institute",
          completionDate: "2024-05-25",
          score: 89
        }
      ],
      requirements: [
        {
          name: "Arc Flash Training",
          completed: false,
          dueDate: "2024-12-01"
        }
      ]
    }
  ];

  // Mock compliance metrics
  const mockMetrics = {
    totalEmployees: 156,
    compliantEmployees: 124,
    expiringSoon: 18,
    nonCompliant: 14,
    departmentBreakdown: [
      { name: "Operations", compliance: 85 },
      { name: "Manufacturing", compliance: 78 },
      { name: "Maintenance", compliance: 92 },
      { name: "Quality Control", compliance: 88 },
      { name: "Safety", compliance: 96 },
      { name: "Administration", compliance: 82 }
    ],
    trainingTrends: [
      { certification: "First Aid/CPR", completions: 24, trend: "up" },
      { certification: "OSHA Training", completions: 18, trend: "down" },
      { certification: "Equipment Safety", completions: 31, trend: "up" },
      { certification: "Hazmat Handling", completions: 12, trend: "stable" }
    ],
    upcomingRenewals: [
      {
        employee: "John Smith",
        certification: "OSHA 30-Hour",
        expiryDate: "Sep 20, 2024",
        daysLeft: 8
      },
      {
        employee: "Mike Chen",
        certification: "Confined Space Entry",
        expiryDate: "Oct 5, 2024",
        daysLeft: 23
      },
      {
        employee: "Lisa Rodriguez",
        certification: "Fire Safety Training",
        expiryDate: "Oct 12, 2024",
        daysLeft: 30
      }
    ]
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
    { id: 'matrix', label: 'Training Matrix', icon: 'Grid3X3' },
    { id: 'reports', label: 'Reports', icon: 'FileText' }
  ];

  const departments = ['all', 'Operations', 'Manufacturing', 'Maintenance', 'Quality Control', 'Safety', 'Administration'];

  const handleViewEmployee = (employee) => {
    setSelectedEmployee(employee);
    setIsEmployeeModalOpen(true);
  };

  const handleUploadCertificate = (employee) => {
    setUploadEmployee(employee);
    setIsUploadModalOpen(true);
  };

  const handleCertificateUpload = (certificateData) => {
    console.log('Certificate uploaded:', certificateData);
    // In a real app, this would update the employee's certifications
  };

  const handleGenerateReport = (reportData) => {
    console.log('Generating report:', reportData);
    // In a real app, this would generate and download the report
  };

  const filteredEmployees = mockEmployees?.filter(employee => {
    const matchesSearch = employee?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         employee?.role?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         employee?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesDepartment = filterDepartment === 'all' || employee?.department === filterDepartment;
    return matchesSearch && matchesDepartment;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Training Compliance</h1>
                <p className="text-muted-foreground mt-2">
                  Manage employee training certificates and ensure regulatory compliance
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="outline" iconName="Upload" iconPosition="left">
                  Bulk Upload
                </Button>
                <Button iconName="Plus" iconPosition="left">
                  Add Employee
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Employees</p>
                  <p className="text-2xl font-bold text-foreground mt-2">{mockMetrics?.totalEmployees}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={24} className="text-primary" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Fully Compliant</p>
                  <p className="text-2xl font-bold text-success mt-2">{mockMetrics?.compliantEmployees}</p>
                </div>
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={24} className="text-success" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Expiring Soon</p>
                  <p className="text-2xl font-bold text-warning mt-2">{mockMetrics?.expiringSoon}</p>
                </div>
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={24} className="text-warning" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Non-Compliant</p>
                  <p className="text-2xl font-bold text-error mt-2">{mockMetrics?.nonCompliant}</p>
                </div>
                <div className="w-12 h-12 bg-error/10 rounded-lg flex items-center justify-center">
                  <Icon name="AlertCircle" size={24} className="text-error" />
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="border-b border-border mb-8">
            <div className="flex space-x-8">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 border-b-2 transition-clinical ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span className="font-medium">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'dashboard' && (
            <ComplianceDashboard metrics={mockMetrics} />
          )}

          {activeTab === 'matrix' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="Search employees..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e?.target?.value)}
                        className="w-full pl-10 pr-4 py-2 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <select
                      value={filterDepartment}
                      onChange={(e) => setFilterDepartment(e?.target?.value)}
                      className="px-3 py-2 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      {departments?.map((dept) => (
                        <option key={dept} value={dept}>
                          {dept === 'all' ? 'All Departments' : dept}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <TrainingMatrix
                employees={filteredEmployees}
                onViewEmployee={handleViewEmployee}
                onUploadCertificate={handleUploadCertificate}
              />
            </div>
          )}

          {activeTab === 'reports' && (
            <ComplianceReports onGenerateReport={handleGenerateReport} />
          )}
        </div>
      </div>
      {/* Modals */}
      <EmployeeDetailModal
        employee={selectedEmployee}
        isOpen={isEmployeeModalOpen}
        onClose={() => {
          setIsEmployeeModalOpen(false);
          setSelectedEmployee(null);
        }}
        onUploadCertificate={handleUploadCertificate}
      />
      <CertificateUploadModal
        employee={uploadEmployee}
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          setUploadEmployee(null);
        }}
        onUpload={handleCertificateUpload}
      />
    </div>
  );
};

export default TrainingCompliance;